import { AxiosError } from "axios";
import { formatInTimeZone } from "date-fns-tz";

import { ERROR_CODES } from "@/constants/errors";

import { DIAL_BLOCK_ET_ZONE } from "./constants";

/** glen's NoRoute answer: what every dial block route says while switched off. */
export const SWITCHED_OFF_ERROR_CODE = 1035;

/** Backend error codes of the dial block routes (`common/errors/errors.go`). */
export const DIAL_BLOCK_ERROR_CODES = {
  START_IN_PAST: 1199,
  START_OFF_SLOT: 1200,
  OUTSIDE_WINDOW: 1201,
  OVERLAP: 1202,
  CAMPAIGN_FULL: 1203,
  CAMPAIGN_NOT_ALLOWED: 1204,
  NOT_FOUND: 1205,
  NOT_CANCELLABLE: 1206,
  BAD_WEEK_START: 1207,
  BUSY: 1208,
  NO_BOOKABLE_HOURS: 1209,
  DAY_NOT_ALLOWED: 1210,
  NOT_CHANGEABLE: 1211,
  NOTHING_TO_CHANGE: 1213,
  PERSONAL_CAP_REACHED: 1215,
  WEEKLY_CEILING_REACHED: 1223,
  BAD_AVAILABILITY_REQUEST: 1224,
  AVAILABILITY_REQUEST_EXISTS: 1225,
  NOT_ON_ROSTER: 1229,
  // Usual commitment requests (C-12 B).
  USUAL_REQUEST_PENDING: 1268,
  BAD_USUAL_REQUEST: 1269,
  // C-16 S8: the campaign's blocks are paused.
  CAMPAIGN_PAUSED: 1333,
} as const;

export interface DialBlockFailureI {
  message: string;
  /** Another change held the caller's blocks; the same request can be retried. */
  isRetryable: boolean;
  /** The week on screen is out of date; refetch it. */
  shouldRefresh: boolean;
}

export const GENERIC_DIAL_BLOCK_FAILURE =
  "Something went wrong. Try again, or reload the page.";

interface FailureContextI {
  campaignName?: string;
  /** The block's start, for the day named in a day-rule refusal. */
  start?: Date;
}

const readError = (error: unknown) => {
  const data = (
    error as AxiosError<{ error_code?: number; error_str?: string }>
  )?.response?.data;

  return {
    code: typeof data?.error_code === "number" ? data.error_code : undefined,
    serverMessage:
      typeof data?.error_str === "string" ? data.error_str : undefined,
  };
};

/**
 * A dial block refusal in plain words for the caller, keyed on the backend's
 * error_code, never its raw message, except where the server's sentence is
 * written for people and carries numbers only it knows: 1201 (the real
 * window), 1215 (the cap), 1223 (the week's ceiling) and 1224 (the request's
 * limits).
 */
export const describeDialBlockFailure = (
  error: unknown,
  { campaignName, start }: FailureContextI = {}
): DialBlockFailureI => {
  const { code, serverMessage } = readError(error);
  const campaign = campaignName || "This campaign";
  const failure = (
    message: string,
    flags: Partial<Omit<DialBlockFailureI, "message">> = {}
  ): DialBlockFailureI => ({
    message,
    isRetryable: false,
    shouldRefresh: false,
    ...flags,
  });

  switch (code) {
    case DIAL_BLOCK_ERROR_CODES.START_IN_PAST:
      return failure("That time has already passed. Pick a later slot.", {
        shouldRefresh: true,
      });
    case DIAL_BLOCK_ERROR_CODES.START_OFF_SLOT:
      return failure("Blocks start on the hour or the half hour (ET).");
    case DIAL_BLOCK_ERROR_CODES.OUTSIDE_WINDOW:
      return failure(
        serverMessage || `This block falls outside ${campaign}'s dialing hours.`
      );
    case DIAL_BLOCK_ERROR_CODES.OVERLAP:
      return failure("You already have a block at that time.", {
        shouldRefresh: true,
      });
    case DIAL_BLOCK_ERROR_CODES.CAMPAIGN_FULL:
      return failure(
        `${campaign} has no blocks left that week. Pick another campaign.`
      );
    case DIAL_BLOCK_ERROR_CODES.CAMPAIGN_NOT_ALLOWED:
      return failure(
        `You can't book blocks on ${campaign}. Ask your LGA if that's wrong.`
      );
    case DIAL_BLOCK_ERROR_CODES.NOT_FOUND:
      return failure("This block no longer exists. Your week was refreshed.", {
        shouldRefresh: true,
      });
    case DIAL_BLOCK_ERROR_CODES.NOT_CANCELLABLE:
      return failure(
        "This block has already started, so it can't be cancelled.",
        {
          shouldRefresh: true,
        }
      );
    case DIAL_BLOCK_ERROR_CODES.BAD_WEEK_START:
      return failure("That week couldn't be loaded. Go back to this week.");
    case DIAL_BLOCK_ERROR_CODES.BUSY:
      return failure(
        "Your blocks are being changed right now. Try again in a moment.",
        { isRetryable: true }
      );
    case DIAL_BLOCK_ERROR_CODES.NO_BOOKABLE_HOURS:
      return failure(
        `${campaign}'s dialing hours are too short for a 3.5-hour block. Ask your LGA to change them.`
      );
    case DIAL_BLOCK_ERROR_CODES.CAMPAIGN_PAUSED:
      return failure(
        `${campaign} isn't taking blocks right now. Pick another campaign.`,
        { shouldRefresh: true }
      );
    case DIAL_BLOCK_ERROR_CODES.DAY_NOT_ALLOWED: {
      const day = start
        ? formatInTimeZone(start, DIAL_BLOCK_ET_ZONE, "EEEE")
        : "that day";

      return failure(
        `${campaign} doesn't call on ${day}. Pick another campaign or day.`
      );
    }
    case DIAL_BLOCK_ERROR_CODES.NOT_CHANGEABLE:
      return failure("This block has already started, so it can't be moved.", {
        shouldRefresh: true,
      });
    case DIAL_BLOCK_ERROR_CODES.NOTHING_TO_CHANGE:
      return failure("Nothing changed.");
    // Written for people with Command Center's wording ("…personal cap…",
    // "You can only book up to N blocks per week (C committed + 3 extra).").
    case DIAL_BLOCK_ERROR_CODES.PERSONAL_CAP_REACHED:
      return failure(
        serverMessage ||
          `You've reached your weekly cap on ${
            campaignName || "this campaign"
          }.`
      );
    case DIAL_BLOCK_ERROR_CODES.WEEKLY_CEILING_REACHED:
      return failure(
        serverMessage || "You've booked as many blocks as you can this week."
      );
    // Update availability (B2c).
    case DIAL_BLOCK_ERROR_CODES.BAD_AVAILABILITY_REQUEST:
      return failure(
        serverMessage ||
          "Check the week, the reason and the number of blocks, then try again."
      );
    case DIAL_BLOCK_ERROR_CODES.AVAILABILITY_REQUEST_EXISTS:
      // Command Center's words.
      return failure("You already have an update for this week", {
        shouldRefresh: true,
      });
    case DIAL_BLOCK_ERROR_CODES.NOT_ON_ROSTER:
      return failure(
        "You aren't on a workspace's roster yet, so there's no LGA to review this."
      );
    // Usual commitment (C-12 B).
    case DIAL_BLOCK_ERROR_CODES.USUAL_REQUEST_PENDING:
      return failure(
        "You already asked to change your usual commitment. Wait for your LGA's answer before asking again.",
        { shouldRefresh: true }
      );
    case DIAL_BLOCK_ERROR_CODES.BAD_USUAL_REQUEST:
      return failure(
        "Pick a usual commitment from 1 to 24 blocks a week, different from the one you have."
      );
    case ERROR_CODES.USER_NOT_APPROVED_FOR_CAMPAIGN:
      return failure(`You're not approved on ${campaign} yet.`);
  }

  return failure(GENERIC_DIAL_BLOCK_FAILURE);
};

/**
 * The kill switch is off: the route isn't registered, so glen's NoRoute
 * answers 404 with error_code 1035 (`glen/server/router.go` NoRoute,
 * ErrorNotFound). A missing block is 404 with 1205, and a 404 with no code at
 * all is some other failure (a proxy, a network edge): neither means "off".
 */
export const isDialBlocksSwitchedOff = (error: unknown): boolean =>
  (error as AxiosError)?.response?.status === 404 &&
  readError(error).code === SWITCHED_OFF_ERROR_CODE;

/** The week's booking ceiling (commitment + 3, B2c) refused a booking. */
export const isWeeklyCeilingRefusal = (error: unknown): boolean =>
  readError(error).code === DIAL_BLOCK_ERROR_CODES.WEEKLY_CEILING_REACHED;
